import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as types from '../types';
import useAdoptionPromoApplied from './useAdoptionPromoApplied';

interface UsePromoCodeEligibilityProps {
  showCoupons: boolean;
}

interface PromoCodeEligible {
  canApplyCode: true;
  ineligibilityReason?: undefined;
}

interface PromoCodeIneligible {
  canApplyCode: false;
  ineligibilityReason: string;
}

export type IndividualPromoCodeEligibility = PromoCodeEligible | PromoCodeIneligible;

interface PromoCodeEligibility {
  couponCodeEligibility: IndividualPromoCodeEligibility;
  giftCardEligibility: IndividualPromoCodeEligibility;
  referralCodeEligibility: IndividualPromoCodeEligibility;
  /** Whether the adoption discount is on the cart and priced, as opposed to merely intended. */
  adoptionPromoApplied: boolean;
}

export default function usePromoCodeEligibility({ showCoupons }: UsePromoCodeEligibilityProps): PromoCodeEligibility {
  const { t } = useTranslation();
  const enableGiftCards = useSelector((state: types.AppState) => !!state.config.siteConfig.enableGiftCards);
  const adoptionPromoApplied = useAdoptionPromoApplied();

  const giftCardEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!enableGiftCards) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('gift_cards_cannot_be_redeemed_at_this_time'),
      };
    }

    return { canApplyCode: true };
  }, [enableGiftCards, t]);

  const referralCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_referral_code_may_not_be_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [showCoupons, t]);

  const couponCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_coupon_may_not_be_applied_to_this_order'),
      };
    }

    // once the adoption discount is on the cart the adopter has no code to type and anything they do
    // type can only displace it. gated on the discount having actually landed rather than on the
    // rescue cookie: an adopter whose auto-apply failed needs the field to recover the gift by hand.
    // only the coupon leg is turned off - the entry field is a shared omnibox, so gift cards and
    // referrals must keep working.
    if (adoptionPromoApplied) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('the_adoption_promo_is_already_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [adoptionPromoApplied, showCoupons, t]);

  return {
    couponCodeEligibility,
    giftCardEligibility,
    referralCodeEligibility,
    adoptionPromoApplied,
  };
}
